import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Banknote, CreditCard, Delete, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatMoney, rovingKeyDown } from './ItemConfiguration'
import type { PosPaymentInput } from './types'
import { CashRegisterNotice, useCashRegister } from './CashRegisterContext'

/** Parse entered money without binary floating-point calculations. */
export function parseTenderedCents(text: string): number | null {
  if (!/^\d{1,7}(?:[.,]\d{1,2})?$/.test(text.trim())) return null
  const [whole, fraction = ''] = text.trim().replace(',', '.').split('.')
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return Number.isSafeInteger(cents) && cents <= 100_000_000 ? cents : null
}

export function PaymentForm({ totalCents, disabled = false, busy = false, onConfirm, onPrepareCard, initialMethod = 'cash' }: {
  totalCents: number; disabled?: boolean; busy?: boolean; onConfirm: (input: PosPaymentInput) => void
  onPrepareCard?: () => void; initialMethod?: 'cash' | 'external_card'
}) {
  const { t, i18n } = useTranslation('posOrdering')
  const register = useCashRegister()
  const cashReady = !register || (!register.unavailable && register.status?.session?.state === 'open')
  const [method, setMethod] = useState<'cash' | 'external_card'>(initialMethod)
  const [tendered, setTendered] = useState('')
  const receivedId = useId()
  const [replaceAmount, setReplaceAmount] = useState(false)
  const [terminalConfirmed, setTerminalConfirmed] = useState(false)
  const cents = method === 'cash' ? parseTenderedCents(tendered) : totalCents
  const valid = totalCents > 0 && cents !== null && cents >= totalCents && (method === 'cash' || terminalConfirmed)
  const money = (amount: number) => formatMoney(i18n.language, amount / 100, 'EUR')
  const setCash = (amount: number) => { setTendered(`${Math.floor(amount / 100)},${String(amount % 100).padStart(2, '0')}`); setReplaceAmount(true) }
  function press(key: string) {
    if (disabled || busy) return
    if (key === 'clear') { setTendered(''); setReplaceAmount(false); return }
    if (key === 'backspace') { setTendered(value => value.slice(0, -1)); setReplaceAmount(false); return }
    let next = replaceAmount ? '' : tendered
    if (key === ',') { if (next.includes(',')) return; next = (next || '0') + ',' }
    else {
      if (!/^[0-9]$/.test(key) || next.split(',')[1]?.length === 2) return
      next = next === '0' ? key : next + key
      if (next.split(',')[0].length > 7) return
    }
    setTendered(next); setReplaceAmount(false)
  }
  const shortcuts = [...new Set([totalCents, Math.ceil(totalCents / 500) * 500, Math.ceil(totalCents / 1000) * 1000, 2000, 5000, 10000])]
    .filter(value => value >= totalCents).sort((a, b) => a - b).slice(0, 4)
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 border-b pb-3">
        <span className="text-muted-foreground">{t('payment.amountDue')}</span>
        <strong className="text-2xl tabular-nums">{money(totalCents)}</strong>
      </div>
      <div role="radiogroup" aria-label={t('payment.method')} className="grid grid-cols-2 gap-2">
        {(['cash', 'external_card'] as const).map(value => {
          const Icon = value === 'cash' ? Banknote : CreditCard
          return <Button key={value} type="button" variant="outline" role="radio" aria-checked={method === value}
            data-roving onKeyDown={event => rovingKeyDown(event, true)}
            tabIndex={method === value ? 0 : -1} disabled={disabled || busy} className={cn('h-12 gap-2', method === value && 'border-primary bg-primary/10 text-primary')}
            onClick={() => { setMethod(value); setTerminalConfirmed(false) }}>
            <Icon aria-hidden className="h-5 w-5" />{t(`payment.${value}`)}
          </Button>
        })}
      </div>
      {method === 'cash' ? (
        <div className="space-y-2">
          <CashRegisterNotice />
          <div role="group" aria-labelledby={receivedId} tabIndex={0}
            className="flex min-h-12 items-center justify-between gap-2 rounded-lg bg-muted px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onKeyDown={event => {
              if (/^[0-9.,]$/.test(event.key) || ['Backspace', 'Delete'].includes(event.key)) {
                event.preventDefault(); press(event.key === 'Backspace' ? 'backspace' : event.key === 'Delete' ? 'clear' : event.key === '.' ? ',' : event.key)
              }
            }}>
            <span id={receivedId} className="text-sm text-muted-foreground">{t('payment.cashReceived')}</span>
            <output aria-live="polite" className="text-xl font-semibold tabular-nums">{tendered || '0,00'}</output>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {shortcuts.map((amount, index) => (
              <Button key={amount} type="button" variant="secondary" disabled={disabled || busy} className="h-11 px-1 text-sm"
                onClick={() => setCash(amount)}>{index === 0 ? t('payment.exact') : money(amount)}</Button>
            ))}
          </div>
          <div role="group" aria-label={t('payment.numberPad')} className="grid grid-cols-3 gap-1.5">
            {['7', '8', '9', '4', '5', '6', '1', '2', '3', ',', '0', 'backspace'].map(key => (
              <Button key={key} type="button" variant="outline" disabled={disabled || busy} className="h-11 text-xl font-semibold tabular-nums"
                aria-label={key === 'backspace' ? t('payment.deleteDigit') : key === ',' ? t('payment.decimal') : undefined}
                onClick={() => press(key)}>{key === 'backspace' ? <Delete aria-hidden className="h-5 w-5" /> : key}</Button>
            ))}
          </div>
          <div className="flex justify-between gap-3 pt-2 text-lg" aria-live="polite">
            <span>{t('payment.change')}</span><strong className="tabular-nums">{cents !== null && cents >= totalCents ? money(cents - totalCents) : '–'}</strong>
          </div>
        </div>
      ) : onPrepareCard ? <p className="text-sm text-muted-foreground">{t('payment.prepareCardHint')}</p> : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('payment.terminalInstructions', { amount: money(totalCents) })}</p>
          <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm font-medium">
            <input type="checkbox" checked={terminalConfirmed} disabled={disabled || busy} className="h-5 w-5 accent-primary"
              onChange={event => setTerminalConfirmed(event.target.checked)} />
            {t('payment.terminalConfirmed')}
          </label>
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t('payment.pilotNotice')}</p>
      <Button type="button" className="h-12 w-full gap-2" disabled={disabled || busy || (method === 'cash' && !cashReady) || (method === 'external_card' && onPrepareCard ? false : !valid)} onClick={() => {
        if (method === 'external_card' && onPrepareCard) { onPrepareCard(); return }
        if (valid && (method !== 'cash' || cashReady)) onConfirm({ idempotencyKey: crypto.randomUUID(), method,
          expectedTotalCents: totalCents, tenderedCents: cents, terminalConfirmed: method === 'external_card' })
      }}>
        {busy ? <Loader2 aria-hidden className="h-5 w-5 animate-spin" /> : method === 'cash'
          ? <Banknote aria-hidden className="h-5 w-5" /> : <CreditCard aria-hidden className="h-5 w-5" />}
        {t(method === 'cash' ? 'payment.confirmCash' : onPrepareCard ? 'payment.prepareCard' : 'payment.confirmCard')}
      </Button>
    </div>
  )
}
